/**
 * AirDrop 式的折射波：把背景畫布當成貼圖，用 WebGL 做透鏡位移和一點色散。
 * 背景永遠只顯示這一張畫布；沒有波動時就是原樣輸出。
 */

export interface WarpWave { x: number; y: number; r: number; amp: number; width: number }

const MAX_WAVES = 4;

const VERT = `
attribute vec2 a;
varying vec2 uv;
void main() {
  uv = vec2(a.x * 0.5 + 0.5, 0.5 - a.y * 0.5);
  gl_Position = vec4(a, 0.0, 1.0);
}`;

// 波峰用高斯曲線的斜率當位移：前緣往外推（放大），後緣往回拉（壓縮），看起來像一道玻璃鼓起來的波。
const FRAG = `
precision mediump float;
uniform sampler2D tex;
uniform vec2 res;
uniform vec4 waves[${MAX_WAVES}];
uniform float widths[${MAX_WAVES}];
varying vec2 uv;
void main() {
  vec2 p = uv * res;
  vec2 off = vec2(0.0);
  float glow = 0.0;
  for (int i = 0; i < ${MAX_WAVES}; i++) {
    vec4 w = waves[i];
    if (w.w == 0.0) continue;
    vec2 d = p - w.xy;
    float dist = max(length(d), 0.001);
    float x = (dist - w.z) / widths[i];
    float prof = exp(-x * x);
    off += (d / dist) * w.w * (-2.0 * x * prof);
    glow += abs(w.w) * prof;
  }
  vec2 q = (p - off) / res;
  vec2 ca = off / res * 0.45;
  float r = texture2D(tex, q + ca).r;
  vec4 c = texture2D(tex, q);
  float b = texture2D(tex, q - ca).b;
  gl_FragColor = vec4(vec3(r, c.g, b) + glow * 0.004, 1.0);
}`;

export class Warp {
  readonly canvas = document.createElement("canvas");
  private gl: WebGLRenderingContext;
  private uRes: WebGLUniformLocation;
  private uWaves: WebGLUniformLocation;
  private uWidths: WebGLUniformLocation;
  private waveData = new Float32Array(MAX_WAVES * 4);
  private widthData = new Float32Array(MAX_WAVES);

  static create(): Warp | null {
    try { return new Warp(); } catch { return null; }
  }

  private constructor() {
    const gl = this.canvas.getContext("webgl", { alpha: false, antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: false });
    if (!gl) throw new Error("no webgl");
    this.gl = gl;
    const prog = gl.createProgram()!;
    for (const [type, src] of [[gl.VERTEX_SHADER, VERT], [gl.FRAGMENT_SHADER, FRAG]] as const) {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? "shader");
      gl.attachShader(prog, s);
    }
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error("link");
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "a");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    // 貼圖原樣上傳，不做色彩空間轉換，跟底下的 2D 畫布顏色一模一樣。
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.uRes = gl.getUniformLocation(prog, "res")!;
    this.uWaves = gl.getUniformLocation(prog, "waves")!;
    this.uWidths = gl.getUniformLocation(prog, "widths")!;
    this.canvas.className = "sky-cv";
  }

  /** 把整張 src 畫布扭曲後畫出來；畫面上只有這一張畫布，不會有對不齊的問題。 */
  render(src: HTMLCanvasElement, w: number, h: number, waves: WarpWave[]) {
    const gl = this.gl;
    if (this.canvas.width !== src.width || this.canvas.height !== src.height) { this.canvas.width = src.width; this.canvas.height = src.height; }
    gl.viewport(0, 0, src.width, src.height);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
    this.waveData.fill(0); this.widthData.fill(1);
    waves.slice(0, MAX_WAVES).forEach((wv, i) => {
      this.waveData.set([wv.x, wv.y, wv.r, wv.amp], i * 4);
      this.widthData[i] = wv.width;
    });
    gl.uniform2f(this.uRes, w, h);
    gl.uniform4fv(this.uWaves, this.waveData);
    gl.uniform1fv(this.uWidths, this.widthData);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

}
